import Vue from 'vue';
import qs from 'qs';
import * as XLSX from 'xlsx';

/**
 * Готовые шаблоны, раздаваемые напрямую из статики
 * (вместо генерации на бэкенде).
 */
const STATIC_TEMPLATES = {
    geo: {
        url: 'static/template_import_geo.xlsx',
        filename: 'template_import_geo.xlsx',
    },
    posm: {
        url: 'static/template_import_posm.xlsx',
        filename: 'template_import_posm.xlsx',
    },
};

/**
 * Схемы импорта: ожидаемые колонки и их типы данных.
 * key - точное имя колонки в шаблоне, type - тип значения.
 */
const VISIT_TO_SHOP_OPTIONS = [
    'Да',
    'Да, ИТ',
    'Нет',
    'Нет координат визита',
];

const SCHEMAS = {
    geo: {
        columns: [
            { key: 'visitUid', type: 'string', required: true },
            { key: 'visitToShop', type: 'string', required: true, enum: VISIT_TO_SHOP_OPTIONS },
        ],
    },
    posm: {
        columns: [
            { key: 'id', type: 'number', required: true },
            { key: 'name', type: 'string', required: true },
            { key: 'programId', type: 'string' },
            { key: 'article', type: 'string' },
            { key: 'isActive', type: 'boolean' },
            { key: 'requiresСonfirmation', type: 'boolean' },
            { key: 'canBeTransferred', type: 'boolean' },
        ],
    },
};

/**
 * Универсальный сервис импорта данных из файлов (.csv / .xlsx).
 * Вся логика импорта (загрузка файла, история, шаблоны) сосредоточена здесь.
 * Шаблоны и правила парсинга для каждого типа данных определяются позже.
 */
class DataImportService {

    constructor () {
        /**
         * Реестр типов импорта.
         * service - имя сервиса на бэкенде (будет уточнено по контракту API).
         */
        this.types = {
            geo: {
                service: 'import-geo',
                title: 'Гео',
            },
            clinics: {
                service: 'import-shops',
                title: 'Клиники',
            },
            shopEmployees: {
                service: 'import-shop-employees',
                title: 'Стоматологи',
            },
            accounts: {
                service: 'import-accounts',
                title: 'Аккаунты',
            },
            posm: {
                service: 'import-posm',
                title: 'POSM',
            },
        };

        this.state = Vue.observable({
            result: null,
            history: {},
            isLoading: false,
            errorFile: null,
        });
    }

    /**
     * Доступ к vuex store через корневой экземпляр приложения
     * @returns {*}
     */
    get store () {
        return (window.getApp && window.getApp.$store) ? window.getApp.$store : null;
    }

    /**
     * Токен авторизации
     * @returns {*}
     */
    get token () {
        return this.store ? this.store.getters['auth/getToken'] : localStorage.apiToken;
    }

    /**
     * Базовый URL API
     * @returns {*}
     */
    get rootUrl () {
        return this.store ? this.store.getters.rootUrl : '';
    }

    /**
     * Базовый URL обменного сервиса (.NET API)
     * @returns {*}
     */
    get netExchangeUrl () {
        return this.store ? this.store.getters.netUrlExchangePath : '';
    }

    /**
     * Базовый URL .NET API (services.adv-ant.ru/<api/api-test>/)
     * @returns {*}
     */
    get netApiUrl () {
        return this.store ? this.store.getters.netUrl : '';
    }

    /**
     * Конфигурация типа импорта
     * @param type
     * @returns {*}
     */
    getType (type) {
        return this.types[type] || null;
    }

    /**
     * Управление индикатором загрузки
     * @param value
     */
    setLoading (value) {
        this.state.isLoading = value;

        if (this.store) {
            this.store.dispatch('loading/isLoading', value, { root: true });
        }
    }

    /**
     * Импорт данных из файла для указанного типа
     * Запрос уходит на обменный сервис .NET: POST data-import?type=<type>
     * Ответ: JSON { records: { total, accepted, aborted: [...] } }
     * и, при наличии отклоненных строк, вторая часть multipart — файл с ошибками.
     * @param type - ключ типа импорта (geo, clinics, shopEmployees, accounts, posm)
     * @param file - выбранный файл (.csv / .xlsx)
     * @returns {Promise<any>}
     */
    importFile (type, file) {
        const config = this.getType(type);

        if (!config) {
            return Promise.reject(new Error(`Unknown import type: ${type}`));
        }

        return new Promise((resolve, reject) => {
            this.setLoading(true);
            this.clearErrorFile();

            const formData = new FormData();
            formData.append('service', config.service);
            formData.append('token', this.token);
            formData.append('file', file);

            axios({
                url: this.netApiUrl + 'data-import?type=' + encodeURIComponent(type),
                method: 'POST',
                responseType: 'blob',
                data: formData,
            }).then(response => {
                const contentType = (response.headers['content-type'] || '').toLowerCase();

                return this.parseImportResponse(response.data, contentType).then(parsed => {
                    this.state.result = parsed.json;
                    this.state.errorFile = parsed.file;
                    this.setLoading(false);
                    window.getApp.$emit('APP_DATA_RECIEVE_SUCCESS', `Данные '${config.title}' успешно импортированы.`);
                    resolve(response);
                });
            }).catch(err => {
                this.setLoading(false);
                console.log('ERROR IN DataImportService | importFile : ' + err);
                window.getApp.$emit('APP_DATA_RECIEVE_ERROR', `Ошибка при импорте данных '${config.title}'!`);
                reject(err);
            });
        });
    }

    /**
     * Получение истории импортов для указанного типа
     * @param type - ключ типа импорта
     * @returns {Promise<any>}
     */
    getImportHistory (type) {
        const config = this.getType(type);

        if (!config) {
            return Promise.reject(new Error(`Unknown import type: ${type}`));
        }

        return new Promise((resolve, reject) => {
            this.setLoading(true);

            axios({
                url: this.netApiUrl + 'data-import/history?type=' + encodeURIComponent(config.service) + '&token=' + encodeURIComponent(this.token),
                method: 'GET',
            }).then(response => {
                const data = Array.isArray(response.data) ? response.data : [response.data];
                Vue.set(this.state.history, type, data);
                this.setLoading(false);
                resolve(response);
            }).catch(err => {
                this.setLoading(false);
                console.log('ERROR IN DataImportService | getImportHistory : ' + err);
                window.getApp.$emit('APP_DATA_RECIEVE_ERROR', `Ошибка при получении истории импортов '${config.title}'!`);
                reject(err);
            });
        });
    }

    /**
     * Сброс результата последнего импорта
     */
    clearResult () {
        this.state.result = null;
        this.clearErrorFile();
    }

    /**
     * Сброс файла с ошибками импорта
     */
    clearErrorFile () {
        if (this.state.errorFile) {
            URL.revokeObjectURL(this.state.errorFile.url);
        }

        this.state.errorFile = null;
    }

    /**
     * Разбор ответа импорта: JSON { records } и опционально файл с ошибками.
     * @param data - blob ответа
     * @param contentType - заголовок Content-Type ответа
     * @returns {Promise<{json: *, file: *}>}
     */
    parseImportResponse (data, contentType) {
        if (contentType.indexOf('multipart') !== -1) {
            return this.parseMultipart(data, contentType).then(parts => {
                let json = null;
                let file = null;

                parts.forEach(part => {
                    if (!json && part.contentType && part.contentType.indexOf('application/json') !== -1) {
                        json = JSON.parse(new TextDecoder('utf-8').decode(part.body));
                    } else if (!file && part.filename) {
                        const blob = new Blob([part.body], {
                            type: part.contentType || 'application/octet-stream',
                        });

                        file = {
                            blob,
                            url: URL.createObjectURL(blob),
                            filename: part.filename,
                        };
                    }
                });

                return { json, file };
            });
        }

        return data.text().then(text => ({ json: JSON.parse(text), file: null }));
    }

    /**
     * Разбор multipart-ответа на части (заголовки + тело)
     * @param blob
     * @param contentType
     * @returns {Promise<Array>}
     */
    parseMultipart (blob, contentType) {
        return blob.arrayBuffer().then(buffer => {
            const bytes = new Uint8Array(buffer);
            const boundary = this.resolveBoundary(bytes, contentType);
            const delimiterBytes = new TextEncoder().encode('--' + boundary);
            const positions = [];
            let index = 0;

            while (index < bytes.length) {
                const position = this.findSequence(bytes, delimiterBytes, index);

                if (position === -1) {
                    break;
                }

                positions.push(position);
                index = position + delimiterBytes.length;
            }

            const parts = [];

            for (let i = 0; i < positions.length - 1; i++) {
                let start = positions[i] + delimiterBytes.length;
                let end = positions[i + 1];

                if (bytes[start] === 0x0d && bytes[start + 1] === 0x0a) {
                    start += 2;
                }

                if (bytes[end - 2] === 0x0d && bytes[end - 1] === 0x0a) {
                    end -= 2;
                }

                parts.push(bytes.slice(start, end));
            }

            return parts.map(part => {
                const headerEnd = this.findSequence(part, [0x0d, 0x0a, 0x0d, 0x0a]);

                if (headerEnd === -1) {
                    return { contentType: null, filename: null, body: part };
                }

                const headerText = new TextDecoder('utf-8').decode(part.slice(0, headerEnd));
                const disposition = headerText.match(/Content-Disposition:[^\r\n]*/i);
                const filenameMatch = disposition
                    ? disposition[0].match(/filename="([^"]*)"/i)
                    : null;
                const contentTypeMatch = headerText.match(/Content-Type:\s*([^\r\n;]+)/i);

                return {
                    contentType: contentTypeMatch ? contentTypeMatch[1].trim().toLowerCase() : null,
                    filename: filenameMatch ? filenameMatch[1] : null,
                    body: part.slice(headerEnd + 4),
                };
            });
        });
    }

    /**
     * Определение boundary multipart-ответа: из тела файла, иначе из Content-Type
     * @param bytes
     * @param contentType
     * @returns {string|null}
     */
    resolveBoundary (bytes, contentType) {
        const crlf = this.findSequence(bytes, [0x0d, 0x0a], 0);

        if (crlf > 0) {
            const firstLine = new TextDecoder('utf-8').decode(bytes.slice(0, crlf));

            if (firstLine.indexOf('--') === 0 && firstLine.length > 2) {
                return firstLine.slice(2);
            }
        }

        const match = contentType.match(/boundary=(?:"([^"]+)"|([^;]+))/i);

        return match ? (match[1] || match[2]).trim() : null;
    }

    /**
     * Поиск последовательности байтов в массиве
     * @param haystack
     * @param needle
     * @param from
     * @returns {number}
     */
    findSequence (haystack, needle, from) {
        const limit = haystack.length - needle.length;

        for (let i = from || 0; i <= limit; i++) {
            let match = true;

            for (let j = 0; j < needle.length; j++) {
                if (haystack[i + j] !== needle[j]) {
                    match = false;
                    break;
                }
            }

            if (match) {
                return i;
            }
        }

        return -1;
    }

    /**
     * Схема импорта для указанного типа
     * @param type - ключ типа импорта
     * @returns {*}
     */
    getSchema (type) {
        return SCHEMAS[type] || null;
    }

    /**
     * Чтение файла в массив строк (первая строка - заголовки)
     * @param file - файл (.csv / .xls / .xlsx)
     * @returns {Promise<{sheetName: string, rows: Array}>}
     */
    parseFile (file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onerror = () => reject(new Error('Не удалось прочитать файл.'));

            reader.onload = e => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array', cellDates: true });
                    const sheetName = workbook.SheetNames.find(name => name === 'Data') || workbook.SheetNames[0];
                    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
                        header: 1,
                        defval: '',
                    });

                    resolve({ sheetName, rows });
                } catch (err) {
                    console.error(err)
                    reject(new Error('Файл не является корректной таблицей (.xlsx/.xls).'));
                }
            };

            reader.readAsArrayBuffer(file);
        });
    }

    /**
     * Валидация файла по схеме импорта: состав колонок и типы данных.
     * @param type - ключ типа импорта
     * @param file - файл (.csv / .xls / .xlsx)
     * @returns {Promise<{valid: boolean, errors: Array, rowCount: number}>}
     */
    validateFile (type, file) {
        const schema = this.getSchema(type);

        if (!schema) {
            return Promise.resolve({ valid: true, errors: [], rowCount: 0 });
        }

        return this.parseFile(file).then(({ sheetName, rows }) => {
            const MAX_ERRORS = 50;
            const errors = [];

            if (!rows.length) {
                return { valid: false, errors: ['Файл не содержит данных.'], rowCount: 0, sheetName };
            }

            const headerRow = rows[0].map(value => String(value).trim());
            const headerIndex = {};

            headerRow.forEach((header, index) => {
                headerIndex[header] = index;
            });

            // Проверка состава колонок
            schema.columns.forEach(col => {
                if (!(col.key in headerIndex)) {
                    this.pushError(errors, MAX_ERRORS, `Не найдена обязательная колонка '${col.key}'.`);
                }
            });

            const extraColumns = headerRow.filter(header => header && !schema.columns.some(col => col.key === header));

            if (extraColumns.length) {
                this.pushError(errors, MAX_ERRORS, `Обнаружены неожиданные колонки: ${extraColumns.join(', ')}.`);
            }

            // Проверка типов данных по строкам
            const dataRows = rows.slice(1);

            dataRows.forEach((row, index) => {
                const rowNumber = index + 2;
                let rowEmpty = true;

                row.forEach(cell => {
                    if (cell !== '' && cell !== null && cell !== undefined) {
                        rowEmpty = false;
                    }
                });

                if (rowEmpty) {
                    return;
                }

                schema.columns.forEach(col => {
                    const raw = row[headerIndex[col.key]];
                    const value = (raw === null || raw === undefined) ? '' : raw;
                    const isEmpty = String(value).trim() === '';

                    if (col.required && isEmpty) {
                        this.pushError(errors, MAX_ERRORS, `Строка ${rowNumber}: колонка '${col.key}' обязательна.`);
                        return;
                    }

                    if (isEmpty) {
                        return;
                    }

                    if (!this.isValidType(value, col.type)) {
                        this.pushError(
                            errors,
                            MAX_ERRORS,
                            `Строка ${rowNumber}: колонка '${col.key}' имеет неверный тип (ожидается: ${this.typeLabel(col.type)}).`
                        );
                    }

                    if (col.enum && col.enum.indexOf(String(value).trim()) === -1) {
                        this.pushError(
                            errors,
                            MAX_ERRORS,
                            `Строка ${rowNumber}: колонка '${col.key}' содержит недопустимое значение '${value}'. Допустимые: ${col.enum.join(', ')}.`
                        );
                    }
                });
            });

            if (errors.length > MAX_ERRORS) {
                errors.push(`... и еще ${errors.length - MAX_ERRORS} ошибок.`);
            }

            return {
                valid: errors.length === 0,
                errors,
                rowCount: dataRows.filter(row => row.some(cell => cell !== '' && cell !== null && cell !== undefined)).length,
                sheetName,
            };
        });
    }

    /**
     * Проверка значения на соответствие ожидаемому типу
     * @param value
     * @param type - string | number | date | boolean
     * @returns {boolean}
     */
    isValidType (value, type) {
        switch (type) {
            case 'number':
                return !isNaN(parseFloat(value)) && isFinite(value);
            case 'date':
                return this.isDate(value);
            case 'boolean':
                return this.isBoolean(value);
            default:
                return true;
        }
    }

    /**
     * Является ли значение корректной датой
     * @param value
     * @returns {boolean}
     */
    isDate (value) {
        if (value instanceof Date) {
            return !isNaN(value.getTime());
        }

        if (typeof value === 'number') {
            return value > 0 && value < 100000;
        }

        const text = String(value).trim();

        if (!text) {
            return false;
        }

        const iso = text.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
        if (iso) {
            return !isNaN(Date.parse(`${iso[1]}-${iso[2]}-${iso[3]}`));
        }

        const ru = text.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
        if (ru) {
            return !isNaN(Date.parse(`${ru[3]}-${ru[2]}-${ru[1]}`));
        }

        return !isNaN(Date.parse(text));
    }

    /**
     * Является ли значение корректным булевым (да/нет, 1/0, true/false)
     * @param value
     * @returns {boolean}
     */
    isBoolean (value) {
        if (typeof value === 'boolean') {
            return true;
        }

        const text = String(value).trim().toLowerCase();

        return ['1', '0', 'true', 'false', 'yes', 'no', 'да', 'нет', 'y', 'n'].indexOf(text) !== -1;
    }

    /**
     * Человекочитаемое имя типа
     * @param type
     * @returns {string}
     */
    typeLabel (type) {
        const labels = {
            string: 'текст',
            number: 'число',
            date: 'дата',
            boolean: 'да/нет (1/0)',
        };

        return labels[type] || type;
    }

    /**
     * Добавление ошибки в список с ограничением максимального количества
     * @param errors
     * @param max
     * @param message
     */
    pushError (errors, max, message) {
        if (errors.length < max) {
            errors.push(message);
        }
    }

    /**
     * Скачивание шаблона для импорта указанного типа
     * Для типов с готовым шаблоном (geo, posm) файл раздается напрямую из статики,
     * для остальных — через API.
     * @param type - ключ типа импорта
     * @returns {Promise<any>}
     */
    downloadTemplate (type) {
        const config = this.getType(type);

        if (!config) {
            return Promise.reject(new Error(`Unknown import type: ${type}`));
        }

        const staticTemplate = STATIC_TEMPLATES[type];

        if (staticTemplate) {
            return new Promise(resolve => {
                this.downloadStaticFile(staticTemplate.url, staticTemplate.filename);
                resolve();
            });
        }

        return new Promise((resolve, reject) => {
            this.setLoading(true);

            axios({
                url: this.rootUrl + 'import/template',
                method: 'POST',
                headers: {
                    'content-type': 'application/x-www-form-urlencoded',
                },
                responseType: 'blob',
                data: qs.stringify({
                    'token': this.token,
                    'service': config.service,
                }),
            }).then(response => {
                this.setLoading(false);
                resolve(response);
            }).catch(err => {
                this.setLoading(false);
                console.log('ERROR IN DataImportService | downloadTemplate : ' + err);
                window.getApp.$emit('APP_DATA_RECIEVE_ERROR', `Ошибка при скачивании шаблона '${config.title}'!`);
                reject(err);
            });
        });
    }

    /**
     * Скачивание файла напрямую (без обращения к API)
     * @param url - адрес файла
     * @param filename - имя сохраняемого файла
     */
    downloadStaticFile (url, filename) {
        const link = document.createElement('a');

        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}

export default new DataImportService();


// WEBPACK FOOTER //
// ./src/services/DataImportService.js